/**
 * Customer 360 strings (`c360.*`), merged into the dictionary through `extra.ts`.
 * `c360En` is typed against `c360Pl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const c360Pl = {
  soon: 'Ta akcja pojawi się wkrótce.',
  notFound: 'Nie znaleziono klienta lub brak dostępu.',
  loadError: 'Nie udało się pobrać danych.',
  loadErrorHint: 'Sprawdź połączenie i spróbuj ponownie.',
  header: {
    erpId: 'ID ERP',
    nip: 'NIP',
    since: 'Klientem od',
    address: 'Adres',
    phone: 'Telefon',
    email: 'E-mail',
    receivingHours: 'Godziny przyjęć',
    owner: 'Opiekun',
    lastContact: 'Ostatni kontakt',
    nextContact: 'Następny kontakt',
    overdue: 'zaległy',
    none: 'brak',
    addTask: 'Dodaj zadanie',
    planVisit: 'Zaplanuj wizytę',
    call: 'Zadzwoń',
    classes: { A: 'Klasa A', B: 'Klasa B', C: 'Klasa C', STRATEGIC: 'Strategiczny' },
  },
  tabs: {
    overview: 'Przegląd',
    sales: 'Sprzedaż',
    products: 'Produkty',
    visits: 'Wizyty',
    documents: 'Dokumenty',
    alerts: 'Alerty',
  },
  kpi: {
    salesMtd: 'Sprzedaż MTD',
    vsSamePeriod: 'vs ten sam okres poprz. miesiąca',
    prevMonth: 'Poprzedni miesiąc',
    prevMonthCaption: 'cały miesiąc, netto',
    twoMonths: '2 mies.: {value}',
    margin: 'Marża MTD',
    marginPp: '{value} p.p.',
    marginCaption: 'vs ten sam okres poprz. mies.',
    margin12m: 'marża 12 mies.',
    orders12m: 'Zamówienia (12 mies.)',
    ordersCaption: 'netto {value}',
    avgOrder: 'Średnie zamówienie',
    avgOrderCaption: 'ostatnie 12 mies.',
    lastOrder: 'Ostatnie zamówienie',
    lastOrderCaption: '{number} · {value}',
    never: 'brak zamówień',
    overdue: 'Należności przeterminowane',
    overdueCaption: '{count} dok. · do zapłaty łącznie {total}',
    noOverdue: 'Brak zaległości · do zapłaty łącznie {total}',
    net12m: 'Sprzedaż 12 mies.',
    net12mCaption: 'korekty: {count} ({value})',
    receipts12mCaption: 'w tym paragony: {value} ({share})',
    asOf: 'Stan na {date}',
  },
  insights: {
    salesDrop: 'Spadek sprzedaży',
    salesDropBody: 'Sprzedaż MTD {current} wobec {previous} w tym samym okresie poprzedniego miesiąca ({delta}).',
    salesGrowth: 'Wzrost sprzedaży',
    salesGrowthBody: 'Sprzedaż MTD {current} wobec {previous} w tym samym okresie poprzedniego miesiąca ({delta}).',
    noOrder: 'Brak zamówienia od {days}',
    noOrderBody: 'Ostatnie zamówienie: {date}. Typowo klient zamawia co ok. {interval}.',
    productLost: 'Klient nie kupuje produktu {name} od {days}',
    productLostBody: 'Zwykle kupował co ok. {interval} (sprzedaż 12 mies.: {net}).',
    moreLost: 'Jeszcze {count} utraconych produktów',
    marginDrop: 'Spadek marży',
    marginDropBody: 'Marża MTD {current} wobec {previous} w tym samym okresie poprzedniego miesiąca ({delta}).',
    receivables: 'Zaległe należności',
    receivablesBody: '{amount} po terminie w {count} dok.',
    seeProducts: 'Zobacz produkty',
    seeDocuments: 'Zobacz dokumenty',
    seeSales: 'Zobacz sprzedaż',
  },
  overview: {
    contacts: 'Kontakty',
    noContacts: 'Brak kontaktów.',
    primary: 'Główny',
    salesChart: 'Sprzedaż — ostatnie 12 miesięcy',
    salesChartSub: 'Netto, zł',
    logistics: 'Logistyka i warunki handlowe',
    logisticsNotes: 'Uwagi logistyczne',
    noLogisticsNotes: 'Brak uwag logistycznych.',
    creditLimit: 'Limit kredytowy',
    paymentTerm: 'Termin płatności',
    paymentTermDays: '{days} dni',
    potential: 'Potencjał',
  },
  sales: {
    chartTitle: 'Sprzedaż i marża — 24 miesiące',
    chartSub: 'Netto (słupki) oraz marża % (linia)',
    yoy: 'Rok do roku',
    seriesNet: 'Sprzedaż netto',
    seriesMargin: 'Marża %',
    seriesPrevYear: 'Rok wcześniej',
    last12: 'Ostatnie 12 mies.',
    prev12: 'Poprzednie 12 mies.',
    yoyChange: 'Zmiana r/r',
    byGroup: 'Sprzedaż wg grup produktów',
    byGroupSub: 'Ostatnie 12 miesięcy, netto',
    noGroups: 'Brak sprzedaży w grupach produktów.',
    otherGroups: 'Pozostałe',
    donutCenter: 'netto, 12 mies.',
    monthsTable: 'Zestawienie miesięczne',
    colMonth: 'Miesiąc',
    colNet: 'Netto',
    colMargin: 'Marża',
    colMarginPct: 'Marża %',
    colOrders: 'Zam.',
    colAvgOrder: 'Śr. zamówienie',
    colMom: 'm/m',
    noSales: 'Brak danych sprzedaży.',
  },
  products: {
    top: 'Najczęściej kupowane produkty',
    topSub: 'Ostatnie 12 miesięcy',
    colProduct: 'Produkt',
    colGroup: 'Grupa',
    colNet: 'Sprzedaż netto',
    colQty: 'Ilość',
    colMarginPct: 'Marża %',
    colPurchases: 'Zakupy',
    colInterval: 'Odstęp',
    colLast: 'Ostatni zakup',
    every: 'co ~{days}',
    showAll: 'Pokaż wszystkie ({count})',
    showLess: 'Pokaż mniej',
    noTop: 'Brak zakupów produktów w ostatnich 12 miesiącach.',
    lost: 'Utracone produkty',
    lostSub: 'Kupowane regularnie, ale nie zamawiane od dłuższego czasu',
    lostSince: 'od {days}',
    lostUsually: 'zwykle co ~{days}',
    lostLastAt: 'ostatnio {date}',
    lostNet: '{value} w 12 mies.',
    noLost: 'Brak utraconych produktów — klient kupuje regularnie.',
    crossSell: 'Cross-sell — propozycje',
    crossSellSub: 'Produkty, które kupują podobni klienci',
    reason: { GROUP_GAP: 'Luka w grupie', PEER_POPULAR: 'Popularne u podobnych' },
    peerAdoption: '{pct} podobnych klientów kupuje ten produkt',
    potential: 'potencjał ~{value}/mies.',
    noCrossSell: 'Brak propozycji cross-sell.',
    coverage: 'Pokrycie grup produktowych',
    coverageSub: 'Ile produktów z grupy kupuje klient',
    coverageOf: 'kupuje {bought} z {total}',
    noCoverage: 'Brak danych o grupach produktowych.',
  },
  visits: {
    title: 'Historia wizyt',
    empty: 'Brak wizyt',
    emptyBody: 'Wizyty tego klienta pojawią się tutaj.',
    status: { PLANNED: 'Zaplanowana', IN_PROGRESS: 'W toku', COMPLETED: 'Zakończona', CANCELLED: 'Anulowana', MISSED: 'Nieodbyta' },
    source: { GPS: 'GPS', MANUAL: 'Ręcznie' },
    duration: '{min} min',
    noNotes: 'Brak notatki.',
    loadMore: 'Pokaż starsze wizyty',
  },
  documents: {
    filterALL: 'Wszystkie',
    filterFV: 'Faktury',
    filterKOR: 'Korekty',
    filterZK: 'Zamówienia',
    colNumber: 'Numer',
    colType: 'Typ',
    colDate: 'Data',
    colNet: 'Netto',
    colGross: 'Brutto',
    colMargin: 'Marża',
    colPayment: 'Płatność',
    colDue: 'Termin',
    empty: 'Brak dokumentów',
    emptyBody: 'Nie znaleziono dokumentów dla wybranego filtra.',
    types: { FV: 'FV', KOR: 'KOR', ZK: 'ZK', PA: 'PA', WZ: 'WZ', OTHER: 'Inny' },
    payment: { UNPAID: 'Nieopłacona', PARTIAL: 'Częściowo', PAID: 'Opłacona', OVERDUE: 'Przeterminowana' },
  },
  alerts: {
    title: 'Alerty klienta',
    empty: 'Brak alertów',
    emptyBody: 'Silnik alertów nie wykrył nic wymagającego uwagi.',
    detected: 'Wykryto {when}',
    status: { OPEN: 'Otwarty', ACK: 'Potwierdzony', RESOLVED: 'Rozwiązany' },
    severity: { CRITICAL: 'Krytyczny', WARNING: 'Ostrzeżenie', INFO: 'Informacja', OPPORTUNITY: 'Szansa' },
    type: {
      SALES_DROP: 'Spadek sprzedaży',
      NO_ORDER: 'Brak zamówienia',
      MARGIN_DROP: 'Spadek marży',
      PRODUCT_LOST: 'Utracony produkt',
      CROSS_SELL: 'Okazja cross-sell',
      VISIT_DUE: 'Wizyta do wykonania',
      DELIVERY_FAILED: 'Nieudana dostawa',
      DELIVERY_LATE: 'Dostawa po terminie',
    },
  },
  list: {
    owner: 'Opiekun',
    allOwners: 'Wszyscy opiekunowie',
    class: 'Klasa',
    allClasses: 'Wszystkie klasy',
    mapTruncated: 'Pokazano {shown} z {total} klientów — zawęź filtry, aby zobaczyć pozostałych.',
    mapCount: '{count} klientów na mapie',
    mapCluster: '{count} klientów — kliknij, aby przybliżyć',
    mapNoOwner: 'Brak opiekuna',
    mapOpen: 'Otwórz kartę klienta',
  },
};

export const c360En: Shape<typeof c360Pl> = {
  soon: 'This action is coming soon.',
  notFound: 'Customer not found or access denied.',
  loadError: 'Could not load the data.',
  loadErrorHint: 'Check your connection and try again.',
  header: {
    erpId: 'ERP ID',
    nip: 'Tax ID',
    since: 'Customer since',
    address: 'Address',
    phone: 'Phone',
    email: 'E-mail',
    receivingHours: 'Receiving hours',
    owner: 'Owner',
    lastContact: 'Last contact',
    nextContact: 'Next contact',
    overdue: 'overdue',
    none: 'none',
    addTask: 'Add task',
    planVisit: 'Plan a visit',
    call: 'Call',
    classes: { A: 'Class A', B: 'Class B', C: 'Class C', STRATEGIC: 'Strategic' },
  },
  tabs: {
    overview: 'Overview',
    sales: 'Sales',
    products: 'Products',
    visits: 'Visits',
    documents: 'Documents',
    alerts: 'Alerts',
  },
  kpi: {
    salesMtd: 'Sales MTD',
    vsSamePeriod: 'vs same period last month',
    prevMonth: 'Previous month',
    prevMonthCaption: 'full month, net',
    twoMonths: '2 months: {value}',
    margin: 'Margin MTD',
    marginPp: '{value} pp',
    marginCaption: 'vs same period last month',
    margin12m: '12-month margin',
    orders12m: 'Orders (12 mo.)',
    ordersCaption: 'net {value}',
    avgOrder: 'Average order',
    avgOrderCaption: 'last 12 months',
    lastOrder: 'Last order',
    lastOrderCaption: '{number} · {value}',
    never: 'no orders',
    overdue: 'Overdue receivables',
    overdueCaption: '{count} docs · total outstanding {total}',
    noOverdue: 'Nothing overdue · total outstanding {total}',
    net12m: 'Sales 12 mo.',
    net12mCaption: 'corrections: {count} ({value})',
    receipts12mCaption: 'incl. receipts: {value} ({share})',
    asOf: 'As of {date}',
  },
  insights: {
    salesDrop: 'Sales drop',
    salesDropBody: 'Sales MTD {current} vs {previous} in the same period last month ({delta}).',
    salesGrowth: 'Sales growth',
    salesGrowthBody: 'Sales MTD {current} vs {previous} in the same period last month ({delta}).',
    noOrder: 'No order for {days}',
    noOrderBody: 'Last order: {date}. The customer typically orders every ~{interval}.',
    productLost: 'Customer has not bought {name} for {days}',
    productLostBody: 'Used to buy about every {interval} (12-month sales: {net}).',
    moreLost: '{count} more lost products',
    marginDrop: 'Margin drop',
    marginDropBody: 'Margin MTD {current} vs {previous} in the same period last month ({delta}).',
    receivables: 'Overdue receivables',
    receivablesBody: '{amount} past due across {count} docs.',
    seeProducts: 'View products',
    seeDocuments: 'View documents',
    seeSales: 'View sales',
  },
  overview: {
    contacts: 'Contacts',
    noContacts: 'No contacts.',
    primary: 'Primary',
    salesChart: 'Sales — last 12 months',
    salesChartSub: 'Net, PLN',
    logistics: 'Logistics & commercial terms',
    logisticsNotes: 'Logistics notes',
    noLogisticsNotes: 'No logistics notes.',
    creditLimit: 'Credit limit',
    paymentTerm: 'Payment term',
    paymentTermDays: '{days} days',
    potential: 'Potential',
  },
  sales: {
    chartTitle: 'Sales and margin — 24 months',
    chartSub: 'Net (bars) and margin % (line)',
    yoy: 'Year over year',
    seriesNet: 'Net sales',
    seriesMargin: 'Margin %',
    seriesPrevYear: 'Previous year',
    last12: 'Last 12 months',
    prev12: 'Previous 12 months',
    yoyChange: 'YoY change',
    byGroup: 'Sales by product group',
    byGroupSub: 'Last 12 months, net',
    noGroups: 'No sales in product groups.',
    otherGroups: 'Other',
    donutCenter: 'net, 12 mo.',
    monthsTable: 'Monthly breakdown',
    colMonth: 'Month',
    colNet: 'Net',
    colMargin: 'Margin',
    colMarginPct: 'Margin %',
    colOrders: 'Orders',
    colAvgOrder: 'Avg. order',
    colMom: 'MoM',
    noSales: 'No sales data.',
  },
  products: {
    top: 'Top products',
    topSub: 'Last 12 months',
    colProduct: 'Product',
    colGroup: 'Group',
    colNet: 'Net sales',
    colQty: 'Quantity',
    colMarginPct: 'Margin %',
    colPurchases: 'Purchases',
    colInterval: 'Interval',
    colLast: 'Last purchase',
    every: 'every ~{days}',
    showAll: 'Show all ({count})',
    showLess: 'Show less',
    noTop: 'No product purchases in the last 12 months.',
    lost: 'Lost products',
    lostSub: 'Bought regularly, but not ordered for a while',
    lostSince: 'for {days}',
    lostUsually: 'usually every ~{days}',
    lostLastAt: 'last on {date}',
    lostNet: '{value} in 12 mo.',
    noLost: 'No lost products — the customer buys regularly.',
    crossSell: 'Cross-sell suggestions',
    crossSellSub: 'Products bought by similar customers',
    reason: { GROUP_GAP: 'Group gap', PEER_POPULAR: 'Popular with peers' },
    peerAdoption: '{pct} of similar customers buy this product',
    potential: 'potential ~{value}/mo.',
    noCrossSell: 'No cross-sell suggestions.',
    coverage: 'Product group coverage',
    coverageSub: 'How many products of each group the customer buys',
    coverageOf: 'buys {bought} of {total}',
    noCoverage: 'No product group data.',
  },
  visits: {
    title: 'Visit history',
    empty: 'No visits',
    emptyBody: 'Visits to this customer will show up here.',
    status: { PLANNED: 'Planned', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', CANCELLED: 'Cancelled', MISSED: 'Missed' },
    source: { GPS: 'GPS', MANUAL: 'Manual' },
    duration: '{min} min',
    noNotes: 'No notes.',
    loadMore: 'Show older visits',
  },
  documents: {
    filterALL: 'All',
    filterFV: 'Invoices',
    filterKOR: 'Corrections',
    filterZK: 'Orders',
    colNumber: 'Number',
    colType: 'Type',
    colDate: 'Date',
    colNet: 'Net',
    colGross: 'Gross',
    colMargin: 'Margin',
    colPayment: 'Payment',
    colDue: 'Due date',
    empty: 'No documents',
    emptyBody: 'No documents found for the selected filter.',
    types: { FV: 'FV', KOR: 'KOR', ZK: 'ZK', PA: 'PA', WZ: 'WZ', OTHER: 'Other' },
    payment: { UNPAID: 'Unpaid', PARTIAL: 'Partial', PAID: 'Paid', OVERDUE: 'Overdue' },
  },
  alerts: {
    title: 'Customer alerts',
    empty: 'No alerts',
    emptyBody: 'The alert engine found nothing that needs attention.',
    detected: 'Detected {when}',
    status: { OPEN: 'Open', ACK: 'Acknowledged', RESOLVED: 'Resolved' },
    severity: { CRITICAL: 'Critical', WARNING: 'Warning', INFO: 'Info', OPPORTUNITY: 'Opportunity' },
    type: {
      SALES_DROP: 'Sales drop',
      NO_ORDER: 'No order',
      MARGIN_DROP: 'Margin drop',
      PRODUCT_LOST: 'Lost product',
      CROSS_SELL: 'Cross-sell opportunity',
      VISIT_DUE: 'Visit due',
      DELIVERY_FAILED: 'Failed delivery',
      DELIVERY_LATE: 'Late delivery',
    },
  },
  list: {
    owner: 'Owner',
    allOwners: 'All owners',
    class: 'Class',
    allClasses: 'All classes',
    mapTruncated: 'Showing {shown} of {total} customers — narrow the filters to see the rest.',
    mapCount: '{count} customers on the map',
    mapCluster: '{count} customers — click to zoom in',
    mapNoOwner: 'No owner',
    mapOpen: 'Open customer card',
  },
};
